/**
 * Router and client-side route guards.
 *
 * These guards are a UX affordance only — the Worker independently enforces
 * auth on every /api/* route. A client-side check is never the security
 * boundary (SECURITY.md §6).
 */
import { useRef } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useSearchParams,
} from 'react-router-dom';
import { authClient } from './auth';
import { postAuthDestination } from './lib/draft';
import { Landing } from './routes/Landing';
import { Auth } from './routes/Auth';
import { Ideas } from './routes/Ideas';
import { ResetPassword } from './routes/ResetPassword';
import { Capture } from './routes/Capture';
import { Launch } from './routes/Launch';
import { IdeaDash } from './routes/IdeaDash';
import { AnnotationOverlay } from './components/AnnotationOverlay';
import { useFeatures } from './lib/features';

export function App() {
  const { data: session, isPending } = authClient.useSession();

  // Only the FIRST session resolution may block rendering.
  //
  // useSession() flips isPending back to true on every REFETCH, and sign-up /
  // sign-in trigger exactly that. Gating the whole router on isPending
  // therefore unmounted the entire route tree mid-flow and remounted it fresh —
  // silently destroying the auth screen's local state. That is why the
  // "Check your inbox" screen never appeared after a masked duplicate sign-up:
  // setPhase('verify_sent') ran, then the component was thrown away before it
  // could paint. Observed in a real browser, not inferred.
  const settledOnce = useRef(false);
  if (!isPending) settledOnce.current = true;

  if (isPending && !settledOnce.current) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--muted)',
          fontSize: 14.5,
        }}
      >
        Loading…
      </div>
    );
  }

  const user = session?.user
    ? {
        name: session.user.name ?? null,
        email: session.user.email,
        emailVerified: Boolean(session.user.emailVerified),
      }
    : null;

  return (
    <>
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/auth"
        element={user ? <RedirectSignedIn /> : <Auth />}
      />
      <Route path="/auth/reset" element={<ResetPassword />} />
      {/* Capture and launch run signed-OUT: the interview is anonymous until
          the scan claims it (Journey spec §05). No guard here on purpose. */}
      <Route path="/capture" element={<Capture />} />
      <Route path="/launch" element={<Launch signedIn={Boolean(user)} />} />
      <Route
        path="/idea/:ideaId"
        element={user ? <IdeaDash /> : <Navigate to="/auth" replace />}
      />
      <Route
        path="/ideas"
        element={user ? <Ideas user={user} /> : <Navigate to="/auth" replace />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Mounted OUTSIDE <Routes>, deliberately.
          Placing it per-route would mean threading a `signedIn` prop into
          screens built without one — Capture takes no session prop at all —
          and would remount the overlay on every navigation, dropping
          in-progress notes. One mount covers every screen, including /capture
          and /launch, which render anonymously but show the pill whenever a
          session exists. */}
      <FeedbackGate signedIn={Boolean(user)} />
    </>
  );
}

/** Renders the overlay only when the server says this user may have it. */
function FeedbackGate({ signedIn }: { signedIn: boolean }) {
  const { feedbackEnabled } = useFeatures(signedIn);
  if (!signedIn || !feedbackEnabled) return null;
  return <AnnotationOverlay />;
}

/**
 * A signed-in visitor to /auth has nothing to do there — but where they go
 * depends on how they arrived. Someone who signed in on another tab mid-
 * interview must land back on /launch to finish the scan, not on /ideas with
 * their draft stranded in localStorage. `next` is resolved through the core
 * allowlist, never used as a path (open-redirect defence); with no valid
 * `next`, the destination is draft-aware — see postAuthDestination().
 */
function RedirectSignedIn() {
  const [params] = useSearchParams();
  return <Navigate to={postAuthDestination(params.get('next'))} replace />;
}

export function Root() {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}
